<template>
  <div>
    <!--
      Auslöser ist ein v-select im Nur-Lese-Modus: Er sieht aus wie die übrigen
      Felder (Label, Rahmen, Fehlerzustand, rules im v-form), öffnet aber den
      Dialog statt einer Klappliste mit rund 270 Einträgen.
    -->
    <!-- Als Knopf, wenn Wählen gleich eine Aktion auslöst (z. B. Disziplin an Waffe hängen) -->
    <v-btn
      v-if="button"
      color="primary"
      :disabled="disabled"
      @click="openDialog"
    >
      <v-icon left>mdi-plus</v-icon>
      {{ buttonText }}
    </v-btn>
    <v-select
      v-else
      :value="hasValue ? value : null"
      :items="hasValue ? [value] : []"
      item-text="LangBez"
      item-value="id"
      return-object
      :label="label"
      :rules="rules"
      :error-messages="errorMessages"
      :disabled="disabled"
      readonly
      outlined
      dense
      hide-details="auto"
      class="discipline-picker-trigger"
      @click.native="openDialog"
      @keydown.native.enter.prevent="openDialog"
      @keydown.native.space.prevent="openDialog"
    >
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center discipline-selection-wrap">
          <div class="d-flex flex-column discipline-selection">
            <span class="font-weight-medium">{{ [item.Nummer, item.KurzBez].filter(Boolean).join(' ') }}</span>
            <span class="text-caption grey--text text--darken-2">{{ item.LangBez }}</span>
          </div>
          <v-chip v-if="bundKurz(item)" x-small outlined class="ml-2 flex-shrink-0">{{ bundKurz(item) }}</v-chip>
        </div>
      </template>
    </v-select>

    <v-dialog
      v-model="dialog"
      :fullscreen="fullscreen"
      max-width="640"
      scrollable
    >
      <v-card :style="fullscreen ? { height: '100%' } : null">
        <v-toolbar flat dense dark color="primary" class="flex-grow-0 flex-shrink-0">
          <v-toolbar-title>{{ label }} auswählen</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark aria-label="Schließen" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div class="px-4 pt-3 pb-2 flex-shrink-0">
          <!-- Zwei Reiter nur, wenn die Waffe eigene Disziplinen hat -->
          <v-btn-toggle
            v-if="hasWeaponTab"
            v-model="activeTab"
            mandatory
            color="primary"
            class="d-flex mb-3"
          >
            <v-btn value="weapon" small class="flex-grow-1 text-none">Zur Waffe ({{ weaponList.length }})</v-btn>
            <v-btn value="all" small class="flex-grow-1 text-none">Alle Disziplinen</v-btn>
          </v-btn-toggle>

          <v-text-field
            v-model="search"
            type="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Nummer, Bezeichnung oder Gruppe suchen"
            aria-label="Disziplin suchen"
            outlined
            dense
            clearable
            hide-details
            @keydown.enter.prevent
          ></v-text-field>

          <!-- Ein Filter mit nur einer Wahl ist keiner – erst ab zwei Bünden -->
          <div v-if="federations.length > 1" class="d-flex flex-wrap align-center mt-2">
            <span class="text-caption grey--text text--darken-1 mr-2">Bund</span>
            <v-chip-group v-model="federation" mandatory column active-class="primary--text">
              <v-chip :value="ALL" small outlined filter>Alle</v-chip>
              <v-chip
                v-for="option in federations"
                :key="option"
                :value="option"
                small
                outlined
                filter
              >
                {{ option }}
              </v-chip>
            </v-chip-group>
          </div>

          <div class="text-caption grey--text text--darken-1 mt-1" aria-live="polite">
            {{ visible.length }} von {{ source.length }}
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-0" :style="fullscreen ? null : { height: '60vh' }">
          <v-list v-if="visible.length" class="py-0">
            <template v-for="(row, index) in visible">
              <v-divider v-if="index > 0" :key="'divider-' + row.id"></v-divider>
              <v-list-item
                :key="row.id"
                :input-value="row.id === selectedId"
                color="primary"
                @click="choose(row)"
              >
                <v-list-item-icon class="mr-3 align-self-center">
                  <v-icon v-if="row.id === selectedId" color="primary">mdi-check</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <!-- Zwei Zeilen statt abschneiden: lange Namen unterscheiden sich oft erst hinten -->
                  <v-list-item-title class="discipline-title">{{ row.label }}</v-list-item-title>
                  <v-list-item-subtitle v-if="row.meta">{{ row.meta }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="row.federation" class="my-0 align-self-center">
                  <v-chip x-small outlined>{{ row.federation }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
          <div v-else class="pa-4 text-body-2 grey--text text--darken-1">
            {{ emptyMessage }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { bundKurz, disciplineLabel, sortDisciplines } from "@/views/component/disciplineUtils";

// Sentinel statt "": v-chip-group behandelt "" als "kein Wert" und nimmt dann den Index
const ALL = "Alle";

function toRow(raw) {
  const label = disciplineLabel(raw);
  const federation = bundKurz(raw);
  const meta = [raw.disziplin_index_name, raw.kaliber].filter(Boolean).join(" · ");
  return {
    raw,
    id: raw.id,
    label,
    federation,
    meta,
    // Gesucht wird über Nummer, Bezeichnungen, Gruppe, Kaliber und Bund
    haystack: [label, meta, federation].join(" ").toLowerCase()
  };
}

/**
 * Disziplin wählen – Dialog mit Suche, Bund-Filter und den Reitern
 * "Zur Waffe" / "Alle Disziplinen" (angelehnt an die v3-Erfassung).
 *
 * v-model ist das Disziplin-Objekt aus dem Backend, wie bisher bei der
 * v-autocomplete mit return-object. Die Liste sortiert selbst.
 */
export default {
  name: "DisciplinePicker",
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    // Disziplinen der gewählten Waffe – leer heißt: kein Reiter "Zur Waffe"
    weaponItems: {
      type: Array,
      default: () => []
    },
    // Gesamtkatalog
    allItems: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: "Disziplin"
    },
    rules: {
      type: Array,
      default: () => []
    },
    errorMessages: {
      type: [String, Array],
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Knopf statt Auswahlfeld als Auslöser
    button: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: "Disziplin hinzufügen"
    }
  },
  data: () => ({
    ALL,
    dialog: false,
    activeTab: "all",
    search: "",
    federation: ALL
  }),
  computed: {
    hasValue() {
      return !!(this.value && this.value.id);
    },
    selectedId() {
      return this.hasValue ? this.value.id : null;
    },
    fullscreen() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    weaponList() {
      return sortDisciplines(this.weaponItems).map(toRow);
    },
    allList() {
      return sortDisciplines(this.allItems).map(toRow);
    },
    hasWeaponTab() {
      return this.weaponList.length > 0;
    },
    source() {
      return this.hasWeaponTab && this.activeTab === "weapon" ? this.weaponList : this.allList;
    },
    // Die Bünde der aktiven Liste, nicht des ganzen Katalogs
    federations() {
      return [...new Set(this.source.map(row => row.federation).filter(Boolean))].sort();
    },
    visible() {
      const needle = (this.search || "").trim().toLowerCase();
      return this.source.filter(row => {
        if (this.federation !== ALL && row.federation !== this.federation) return false;
        return !needle || row.haystack.includes(needle);
      });
    },
    emptyMessage() {
      if (this.search || this.federation !== ALL) return "Keine Disziplin passt zu dieser Suche.";
      return "Es sind keine Disziplinen hinterlegt.";
    }
  },
  watch: {
    // Ein Bund-Filter, der auf der anderen Liste nichts trifft, sähe aus wie eine leere Liste
    activeTab() {
      this.federation = ALL;
    }
  },
  methods: {
    bundKurz,
    openDialog() {
      if (this.disabled) return;
      this.search = "";
      this.federation = ALL;
      this.activeTab = this.hasWeaponTab ? "weapon" : "all";
      this.dialog = true;
    },
    choose(row) {
      this.$emit("input", row.raw);
      this.$emit("change", row.raw);
      this.dialog = false;
    }
  }
};
</script>

<style scoped>
.discipline-picker-trigger ::v-deep .v-input__slot,
.discipline-picker-trigger ::v-deep input {
  cursor: pointer;
}
.discipline-picker-trigger ::v-deep .v-select__slot input {
  align-self: center;
}
.discipline-selection-wrap {
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;
}
.discipline-selection {
  min-width: 0;
  flex: 1 1 auto;
  line-height: 1.15;
}
.discipline-selection > span {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.discipline-title {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}
</style>
